// @ts-check

if (!customElements.get("ff-facets")) {
  class FfFacets extends HTMLElement {
    ffNewUrl = new URL(window.location.href);
    /** @type {AbortController|null} */
    ffAbortController = null;
    /** @type {HTMLInputElement|null} */
    ffFiltersToggle = null;
    /** @type {HTMLElement|null} */
    ffFiltersContainer = null;
    /** @type {MediaQueryList|null} */
    ffMobileQuery = null;
    /** @type {IntersectionObserver|null} */
    ffInfiniteScrollObserver = null;
    /** @type {boolean} */
    ffIsLoadingMore = false;

    constructor() {
      super();

      this.ffPreventFormSubmit();
      this.ffAddEvents(this);
      this.ffUpdateProductCount();
      this.ffUpdateActiveFiltersCount();
      this.ffInitMobileFilters();
      this.ffInitFilterSearch();
      this.ffInitInfiniteScroll();
      this.ffInitFilterTabListener();
      this.ffInitPriceRangeListener();

      window.addEventListener('load', () => {
        this.ffUpdateProductCount();
        this.ffUpdateActiveFiltersCount();
      });
    }

    /** Prevent native form submit when JS is active (filters use AJAX instead) */
    ffPreventFormSubmit = () => {
      const form = this.querySelector('.ff-filters-form');
      if (form) form.addEventListener('submit', (e) => e.preventDefault());
    };

    /**
     * Update the active filters count displayed in the trigger button.
     * Counts URL params that are filter-related (filter.* params).
     */
    ffUpdateActiveFiltersCount = () => {
      const countEl = document.querySelector('[data-ff-active-filters-count]');
      if (!countEl) return;

      const url = new URL(window.location.href);
      let count = 0;

      // Count filter params (filter.v.*, filter.p.*)
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') && value) {
          count++;
        }
      });

      countEl.textContent = `(${count})`;
    };

    /**
     * Listen for filter tab changes from ff-header-nav-buttons component.
     * Applies the new URL when a filter tab is clicked.
     */
    ffInitFilterTabListener = () => {
      document.addEventListener('ff-filter-tab:change', (e) => {
        const event = /** @type {CustomEvent<{url: URL}>} */ (e);
        if (event.detail?.url) {
          this.ffNewUrl = event.detail.url;
          this.ffApplyUrl(this.ffNewUrl);
        }
      });
    };

    /**
     * Listen for price range filter changes.
     * Debounces input changes and updates URL with price parameters.
     * AbortController in ffApplyUrl handles cancellation of in-flight requests.
     */
    ffInitPriceRangeListener = () => {
      const priceRange = /** @type {HTMLElement|null} */ (this.querySelector('[data-ff-price-range]'));
      if (!priceRange) return;

      const minInput = /** @type {HTMLInputElement|null} */ (priceRange.querySelector('[data-ff-price-min]'));
      const maxInput = /** @type {HTMLInputElement|null} */ (priceRange.querySelector('[data-ff-price-max]'));
      const progress = /** @type {HTMLElement|null} */ (priceRange.querySelector('[data-ff-price-progress]'));
      const minLabel = /** @type {HTMLElement|null} */ (priceRange.querySelector('[data-ff-price-label-min]'));
      const maxLabel = /** @type {HTMLElement|null} */ (priceRange.querySelector('[data-ff-price-label-max]'));

      if (!minInput || !maxInput) return;

      const rangeMin = parseFloat(priceRange.dataset.rangeMin || '0');
      const rangeMax = parseFloat(priceRange.dataset.rangeMax || '10000');
      const currencySymbol = priceRange.dataset.currencySymbol || '€';

      /** @type {ReturnType<typeof setTimeout>|null} */
      let priceTimeout = null;

      /**
       * Format number as money (simple formatting)
       * @param {number} value
       */
      const formatMoney = (value) => {
        return `${value.toLocaleString()}${currencySymbol}`;
      };

      /**
       * Update the progress bar position between the two thumbs
       */
      const updateProgress = () => {
        if (!progress) return;
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);
        const range = rangeMax - rangeMin;
        const leftPercent = ((minVal - rangeMin) / range) * 100;
        const rightPercent = ((maxVal - rangeMin) / range) * 100;
        progress.style.left = `${leftPercent}%`;
        progress.style.width = `${rightPercent - leftPercent}%`;
      };

      /**
       * Update the price labels
       */
      const updateLabels = () => {
        if (minLabel) minLabel.textContent = formatMoney(parseFloat(minInput.value));
        if (maxLabel) maxLabel.textContent = formatMoney(parseFloat(maxInput.value));
      };

      /**
       * Handle min slider change - ensure it doesn't exceed max
       */
      const handleMinChange = () => {
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);
        if (minVal > maxVal) {
          minInput.value = String(maxVal);
        }
        updateProgress();
        updateLabels();
        scheduleUrlUpdate();
      };

      /**
       * Handle max slider change - ensure it doesn't go below min
       */
      const handleMaxChange = () => {
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);
        if (maxVal < minVal) {
          maxInput.value = String(minVal);
        }
        updateProgress();
        updateLabels();
        scheduleUrlUpdate();
      };

      /**
       * Debounced URL update
       */
      const scheduleUrlUpdate = () => {
        if (priceTimeout) clearTimeout(priceTimeout);
        priceTimeout = setTimeout(() => {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('filter.v.price.gte');
          newUrl.searchParams.delete('filter.v.price.lte');
          const minVal = parseFloat(minInput.value);
          const maxVal = parseFloat(maxInput.value);
          // Only set params if different from range bounds
          if (minVal > rangeMin) newUrl.searchParams.set('filter.v.price.gte', String(minVal));
          if (maxVal < rangeMax) newUrl.searchParams.set('filter.v.price.lte', String(maxVal));
          this.ffNewUrl = newUrl;
          this.ffApplyUrl(this.ffNewUrl);
        }, 400);
      };

      // Initialize progress and labels
      updateProgress();
      updateLabels();

      // Add event listeners
      minInput.addEventListener('input', handleMinChange);
      maxInput.addEventListener('input', handleMaxChange);
    };

    /**
     * Initialize mobile filters toggle behavior.
     * Listens to checkbox input and handles height transitions.
     */
    ffInitMobileFilters = () => {
      const mobileBreakpoint = this.dataset.ffMobileBreakpoint || '768';
      this.ffMobileQuery = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);

      this.ffFiltersToggle = this.querySelector('input[id^="ff-filters-toggle"]');
      this.ffFiltersContainer = this.querySelector('.ff-collection-filters');

      if (!this.ffFiltersToggle || !this.ffFiltersContainer) return;

      this.ffFiltersToggle.addEventListener('change', this.ffHandleFiltersToggle);
      this.ffFiltersContainer.addEventListener('transitionend', this.ffHandleFiltersTransitionEnd);

      // Sync ff-button-active class with checkbox state
      this.ffSyncFilterButtonState();
      this.ffFiltersToggle.addEventListener('change', this.ffSyncFilterButtonState);
    };

    /**
     * Handle filters toggle checkbox change.
     * Only applies vertical collapsible behavior on mobile.
     */
    ffHandleFiltersToggle = () => {
      if (!this.ffMobileQuery?.matches || !this.ffFiltersToggle || !this.ffFiltersContainer) return;

      const isOpening = this.ffFiltersToggle.checked;
      const filtersContent = this.querySelector('.ff-collection-filters-content-wrapper');

      if (isOpening) {
        // Calculate and set height for opening animation
        if (filtersContent) {
          const height = filtersContent.scrollHeight;
          this.style.setProperty('--ff-filters-content-height', `${height}px`);
        }
        this.ffFiltersContainer.classList.add('ff-filters--opening');
      } else {
        // Set explicit height before closing (needed for transition from auto)
        if (filtersContent) {
          const currentHeight = filtersContent.scrollHeight;
          this.style.setProperty('--ff-filters-content-height', `${currentHeight}px`);
        }
        this.ffFiltersContainer.classList.add('ff-filters--closing');
        // Force reflow then animate to 0
        requestAnimationFrame(() => {
          this.style.setProperty('--ff-filters-content-height', '0px');
        });
      }
    };

    /**
     * Handle filters container transitionend.
     * Removes pointer-events restriction and sets height to auto when open.
     * @param {TransitionEvent} e
     */
    ffHandleFiltersTransitionEnd = (e) => {
      if (e.propertyName !== 'height' || e.target !== this.ffFiltersContainer) return;
      if (!this.ffFiltersContainer || !this.ffFiltersToggle) return;

      if (this.ffFiltersToggle.checked) {
        // Opening complete - set height to auto for internal accordions
        this.style.setProperty('--ff-filters-content-height', 'auto');
      }

      this.ffFiltersContainer.classList.remove('ff-filters--opening', 'ff-filters--closing');
    };

    /**
     * Sync the ff-button-active class on the trigger button with the checkbox state.
     * Finds the label with for="{{ ff_filters_toggle_id }}" and toggles the active class
     * on the .ff-snippet-button inside it.
     */
    ffSyncFilterButtonState = () => {
      if (!this.ffFiltersToggle) return;

      const toggleId = this.ffFiltersToggle.id;
      /** @type {HTMLLabelElement|null} */
      const triggerLabel = document.querySelector(`label[for="${toggleId}"]`);

      if (!triggerLabel) return;

      /** @type {HTMLElement|null} */
      const triggerButton = triggerLabel.querySelector('.ff-snippet-button');

      if (!triggerButton) return;

      // Toggle ff-button-active class based on checkbox state
      if (this.ffFiltersToggle.checked) {
        triggerButton.classList.add('ff-button-active');
      } else {
        triggerButton.classList.remove('ff-button-active');
      }
    };

    /**
     * Initialize filter search inputs within filter categories.
     * Handles search input, accordion expansion/locking, and option hiding.
     * Uses dynamic queries (not closures) to support DOM replacement.
     */
    ffInitFilterSearch = () => {
      /** @type {NodeListOf<HTMLInputElement>} */
      const searchInputs = this.querySelectorAll('[data-ff-filter-search]');

      searchInputs.forEach(input => {
        /** @type {any} */
        const inputEl = input;
        if (inputEl._ffSearchInitialized) return;
        inputEl._ffSearchInitialized = true;

        // Only capture paramName - all other elements are queried dynamically
        // to handle DOM replacement via Section Rendering API
        const paramName = input.dataset.ffFilterSearch;
        const showMoreId = paramName ? `filter-more-${paramName.replace(/\./g, '-')}` : null;

        input.addEventListener('input', () => {
          const searchValue = input.value.toLowerCase().trim();

          // Query elements dynamically (not from closure) to handle DOM replacement
          /** @type {HTMLElement|null} */
          const category = input.closest('ff-accordion');
          if (!category) return;

          /** @type {HTMLElement|null} */
          const showMoreAccordion = showMoreId ? category.querySelector(`ff-accordion[data-ff-accordion-id="${showMoreId}"]`) : null;

          // Clear previous hide classes from category
          const hideClasses = Array.from(category.classList).filter(c => c.startsWith('ff-hide-'));
          hideClasses.forEach(c => category.classList.remove(c));

          if (searchValue.length > 0) {
            // Expand show-more accordion and lock it with CSS class
            if (showMoreAccordion) {
              const accordionContent = showMoreAccordion.querySelector('[data-ff-accordion-content]');
              if (accordionContent && !accordionContent.classList.contains('ff-accordion--active')) {
                /** @type {any} */ (showMoreAccordion).ffToggle?.(true);
              }
              showMoreAccordion.classList.add('ff-accordion--search-locked');
            }

            // Add search-active class to category
            category.classList.add('ff-search-active');

            // Collect all options and determine which to hide
            /** @type {NodeListOf<HTMLElement>} */
            const allOptions = category.querySelectorAll('.ff-filter-option');
            allOptions.forEach(option => {
              const label = option.textContent?.toLowerCase() || '';
              if (!label.includes(searchValue)) {
                // Get the option's unique class (ff-opt-*)
                const optClass = Array.from(option.classList).find(c => c.startsWith('ff-opt-'));
                if (optClass) {
                  // Add hide class to category container - CSS rule already exists from Liquid
                  category.classList.add(`ff-hide-${optClass}`);
                }
              }
            });
          } else {
            // Remove lock and search-active class
            if (showMoreAccordion) {
              showMoreAccordion.classList.remove('ff-accordion--search-locked');
            }
            category.classList.remove('ff-search-active');
            // Hide classes already cleared above
          }
        });
      });
    };

    /**
     * Initialize infinite scroll if pagination mode is set to 'infinite'.
     * Uses Intersection Observer to detect when sentinel is visible.
     */
    ffInitInfiniteScroll = () => {
      if (this.dataset.ffPaginationMode !== 'infinite') return;

      // Create observer with rootMargin to trigger well before sentinel is visible
      // 800px = roughly 2 rows of products before reaching end
      this.ffInfiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.ffIsLoadingMore) {
              this.ffLoadMoreProducts();
            }
          });
        },
        { rootMargin: '800px' }
      );

      this.ffObserveSentinel();
    };

    /**
     * Start observing the infinite scroll sentinel element.
     * Called after initialization and after filter/sort changes.
     */
    ffObserveSentinel = () => {
      if (!this.ffInfiniteScrollObserver) return;

      // Disconnect previous observations
      this.ffInfiniteScrollObserver.disconnect();

      /** @type {HTMLElement|null} */
      const sentinel = this.querySelector('.ff-infinite-scroll-sentinel[data-ff-next-page]');
      if (sentinel) {
        this.ffInfiniteScrollObserver.observe(sentinel);
      }
    };

    /**
     * Load more products for infinite scroll.
     * Fetches next page and appends products to the grid.
     */
    ffLoadMoreProducts = async () => {
      /** @type {HTMLElement|null} */
      const sentinel = this.querySelector('.ff-infinite-scroll-sentinel');
      if (!sentinel || !sentinel.dataset.ffNextPage || this.ffIsLoadingMore) return;

      this.ffIsLoadingMore = true;
      sentinel.classList.add('ff-loading');

      try {
        const nextPageUrl = new URL(sentinel.dataset.ffNextPage, window.location.origin);
        const response = await fetch(nextPageUrl);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');

        // Get new products from the fetched page
        /** @type {HTMLElement|null} */
        const newGrid = html.querySelector('.ff-product-grid-container-js');
        /** @type {HTMLElement|null} */
        const currentGrid = this.querySelector('.ff-product-grid-container-js');

        if (newGrid && currentGrid) {
          // Append each product card to the current grid
          const newProducts = newGrid.querySelectorAll('.ff-product-card-block');
          newProducts.forEach(product => {
            currentGrid.appendChild(product);
          });
        }

        // Update sentinel with next page info
        /** @type {HTMLElement|null} */
        const newSentinel = html.querySelector('.ff-infinite-scroll-sentinel');
        if (newSentinel) {
          // Update data attributes
          if (newSentinel.dataset.ffNextPage) {
            sentinel.dataset.ffNextPage = newSentinel.dataset.ffNextPage;
          } else {
            // No more pages - remove the attribute to hide sentinel
            delete sentinel.dataset.ffNextPage;
          }
          sentinel.dataset.ffCurrentPage = newSentinel.dataset.ffCurrentPage || '';
          sentinel.dataset.ffTotalPages = newSentinel.dataset.ffTotalPages || '';
        } else {
          // No sentinel in response means no more pages
          delete sentinel.dataset.ffNextPage;
        }

        // Update product count
        this.ffUpdateProductCount();

        // Re-observe sentinel if there are more pages
        this.ffObserveSentinel();

      } catch (error) {
        console.error('Failed to load more products:', error);
      } finally {
        this.ffIsLoadingMore = false;
        sentinel.classList.remove('ff-loading');
      }
    };

    /**
     * Scroll to the top of the product grid with smooth animation.
     * Provides better UX when changing pages - user sees new products loading.
     */
    ffScrollToGrid = () => {
      /** @type {HTMLElement|null} */
      const productGrid = document.querySelector('.ff-product-grid-js');
      if (!productGrid) return;

      const gridRect = productGrid.getBoundingClientRect();
      const scrollTop = window.scrollY + gridRect.top;

      // Offset to account for sticky header (if any)
      const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0', 10);

      window.scrollTo({
        top: Math.max(0, scrollTop - headerOffset - 20),
        behavior: 'smooth'
      });
    };

    /**
     * Add loading state - marks both ff-facets and the target element
     * @param {HTMLElement} targetElement - The element that triggered the loading
     */
    ffAddLoadingState = (targetElement) => {
      this.classList.add('ff-loading');
      targetElement.dataset.ffLoadingTarget = 'true';
    };

    /**
     * Remove all loading states
     */
    ffClearLoadingState = () => {
      this.classList.remove('ff-loading');
      this.querySelectorAll('[data-ff-loading-target]').forEach(el => {
        delete /** @type {HTMLElement} */ (el).dataset.ffLoadingTarget;
      });
    };

    /**
     * Attach event listeners to elements within a container
     * @param {HTMLElement} container - The container element to search within
     */
    ffAddEvents = (container) => {
      // Sort buttons
      container.querySelectorAll('[data-ff-sort-option]').forEach(btn => {
        if (btn._ffSortInitialized) return;
        btn._ffSortInitialized = true;

        btn.addEventListener('click', () => {
          const sortValue = btn.dataset.ffSortOption;

          // Add loading state for shimmer effect
          this.ffAddLoadingState(/** @type {HTMLElement} */ (btn));

          document.querySelectorAll('[data-ff-sort-option]').forEach(b => {
            b.classList.remove('ff-button-active');
            b.removeAttribute('data-ff-active');
          });
          btn.classList.add('ff-button-active');
          btn.setAttribute('data-ff-active', 'true');

          this.ffNewUrl.searchParams.set('sort_by', sortValue);
          this.ffApplyUrl(this.ffNewUrl);
        });
      });

      // Filter checkboxes
      /** @type {NodeListOf<HTMLElement>} */
      const filters = container.querySelectorAll("[data-ff-filter-value]");
      filters.forEach((filter) => {
        if (filter._ffFilterInitialized) return;
        filter._ffFilterInitialized = true;

        filter.addEventListener("change", () => {
          const { ffFilterValue, ffFilterName } = filter.dataset;

          if (!ffFilterValue || !ffFilterName) {
            throw new Error("Filter is missing name or value in dataset");
          }

          // Add loading state for shimmer effect
          const filterOption = filter.closest('.ff-filter-option');
          if (filterOption) {
            this.ffAddLoadingState(/** @type {HTMLElement} */ (filterOption));
          }

          if (this.ffNewUrl.searchParams.has(ffFilterName, ffFilterValue)) {
            this.ffNewUrl.searchParams.delete(ffFilterName, ffFilterValue);
          } else {
            this.ffNewUrl.searchParams.append(ffFilterName, ffFilterValue);
          }

          this.ffApplyUrl(this.ffNewUrl);
        });
      });

      // Price range filters
      /** @type {NodeListOf<HTMLInputElement>} */
      const priceFilters = container.querySelectorAll("[data-ff-filter-price-name]");
      priceFilters.forEach((priceFilter) => {
        if (priceFilter._ffPriceInitialized) return;
        priceFilter._ffPriceInitialized = true;

        priceFilter.addEventListener("change", () => {
          this.adjustToValidValues(priceFilter);
          this.setMinAndMaxValues(priceFilters);
          const { ffFilterPriceName } = priceFilter.dataset;

          this.ffNewUrl.searchParams.set(ffFilterPriceName, priceFilter.value);
          this.ffApplyUrl(this.ffNewUrl);
        });

        priceFilter.addEventListener("keydown", (event) => {
          if (event.metaKey) return;

          const pattern =
            /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
          if (!event.key.match(pattern)) event.preventDefault();
        });

        const { ffFilterPriceName } = priceFilter.dataset;
        this.ffNewUrl.searchParams.set(ffFilterPriceName, priceFilter.value);
      });

      // Pagination buttons
      container.querySelectorAll('[data-ff-page-url]').forEach(btn => {
        btn.addEventListener('click', () => {
          const btnEl = /** @type {HTMLElement} */ (btn);

          // Skip if button is disabled
          if (btnEl.getAttribute('aria-disabled') === 'true') return;

          const pageUrl = new URL(btnEl.dataset.ffPageUrl || '', window.location.origin);

          // Check if AJAX pagination is enabled (default: true)
          /** @type {HTMLElement|null} */
          const paginationNav = btnEl.closest('.ff-pagination');
          const useAjax = paginationNav?.dataset.ffAjaxPagination !== 'false';

          if (!useAjax) {
            // Traditional navigation - full page reload
            window.location.href = pageUrl.href;
            return;
          }

          // AJAX pagination
          const pageParam = pageUrl.searchParams.get('page');

          if (pageParam) {
            this.ffNewUrl.searchParams.set('page', pageParam);
          }

          // Add loading state to pagination container
          if (paginationNav) {
            this.ffAddLoadingState(paginationNav);
          }

          // Scroll to top of product grid for better UX
          this.ffScrollToGrid();

          this.ffApplyUrl(this.ffNewUrl, true);
        });
      });
    };

    ffUpdateProductCount = () => {
      requestAnimationFrame(() => {
        const counter = document.querySelector('[id*="ff-products-count"] .ff-snippet-text');
        if (!counter) return;

        const targetGrid = document.querySelector('.ff-product-grid-container-js');
        if (!targetGrid) return;

        const mobileBreakpoint = parseInt(this.dataset.ffMobileBreakpoint || '768');
        const isMobile = window.innerWidth <= mobileBreakpoint;
        const selector = isMobile
          ? '.ff-card-product:not(.ff-d-none--mb)'
          : '.ff-card-product';

        const visibleCount = targetGrid.querySelectorAll(selector).length;
        const totalCount = this.dataset.ffFilteredCount || this.dataset.ffTotalCount || '0';
        const translatedText = this.dataset.ffTranslatedText || '';

        if (visibleCount > 0 && translatedText) {
          const text = translatedText
            .replace('[%RESULTS_COUNT%]', `<b>${visibleCount}</b>`)
            .replace('[%PRODUCTS_TOTAL%]', `<b>${totalCount}</b>`);
          counter.innerHTML = `<p>${text}</p>`;
        }

        // Update standalone total products count
        const totalNumEl = document.querySelector('[data-ff-total-products-number]');
        if (totalNumEl) {
          totalNumEl.textContent = this.dataset.ffTotalCount || '0';
        }
      });
    };

    /**
     * Update filters in place without full DOM replacement
     * Falls back to replacing individual accordions if new options detected
     * @param {Document} newDoc - Parsed HTML document with new filter state
     */
    ffUpdateFiltersInPlace = (newDoc) => {
      // 1. Update sort buttons (these never change in quantity)
      /** @type {NodeListOf<HTMLElement>} */
      const sortButtons = this.querySelectorAll('[data-ff-sort-option]');
      sortButtons.forEach(currentBtn => {
        const sortValue = currentBtn.dataset.ffSortOption;
        /** @type {HTMLElement|null} */
        const newBtn = newDoc.querySelector(`[data-ff-sort-option="${sortValue}"]`);

        if (newBtn) {
          const isActive = newBtn.dataset.ffActive === 'true';
          currentBtn.classList.toggle('ff-button-active', isActive);
          if (isActive) {
            currentBtn.dataset.ffActive = 'true';
          } else {
            delete currentBtn.dataset.ffActive;
          }
        }
      });

      // 2. Update filter categories (top-level accordions only)
      // Skip "filter-more-*" accordions - they are nested inside category accordions
      // and will be replaced as part of their parent's content
      /** @type {NodeListOf<HTMLElement>} */
      const accordions = this.querySelectorAll('ff-accordion[data-ff-accordion-id^="filter-"]:not([data-ff-accordion-id^="filter-more-"])');
      accordions.forEach(currentAccordion => {
        const accordionId = currentAccordion.dataset.ffAccordionId;
        /** @type {HTMLElement|null} */
        const newAccordion = newDoc.querySelector(`ff-accordion[data-ff-accordion-id="${accordionId}"]`);

        if (!newAccordion) {
          // Category no longer exists - hide it
          currentAccordion.style.display = 'none';
          return;
        }

        // Always replace accordion content (preserving search wrapper)
        // This ensures DOM stays in sync with server HTML structure
        /** @type {HTMLElement|null} */
        const newContent = newAccordion.querySelector('[data-ff-accordion-content]');
        /** @type {HTMLElement|null} */
        const currentContent = currentAccordion.querySelector('[data-ff-accordion-content]');

        if (newContent && currentContent) {
          // Preserve search wrapper (must not be replaced - keeps input value and event listeners)
          /** @type {HTMLElement|null} */
          const currentSearchWrapper = currentContent.querySelector('.ff-filter-search-wrapper');
          /** @type {HTMLInputElement|null} */
          const currentSearchInput = currentSearchWrapper ? currentSearchWrapper.querySelector('[data-ff-filter-search]') : null;
          const searchValue = currentSearchInput?.value || '';
          const isSearchActive = currentAccordion.classList.contains('ff-search-active');

          // Preserve accordion open state
          const wasOpen = currentContent.classList.contains('ff-accordion--active');
          const currentHeight = currentContent.style.height;

          // Replace content with server HTML
          currentContent.innerHTML = newContent.innerHTML;

          // Restore search wrapper
          if (currentSearchWrapper) {
            /** @type {HTMLElement|null} */
            const newSearchWrapper = currentContent.querySelector('.ff-filter-search-wrapper');
            if (newSearchWrapper) {
              newSearchWrapper.replaceWith(currentSearchWrapper);
            } else {
              currentContent.insertBefore(currentSearchWrapper, currentContent.firstChild);
            }
          }

          // Re-trigger search if it was active (handles show-more locking)
          if (isSearchActive && searchValue && currentSearchInput) {
            currentAccordion.classList.add('ff-search-active');
            currentSearchInput.dispatchEvent(new Event('input'));
          }

          // Restore open state
          if (wasOpen) {
            currentContent.classList.add('ff-accordion--active');
            currentContent.style.height = currentHeight === 'auto' ? 'auto' : currentContent.scrollHeight + 'px';
          }

          // Re-attach events to new content
          this.ffAddEvents(currentContent);

          // Reinit ALL nested accordions (including locked ones - lock is CSS-only)
          currentContent.querySelectorAll('ff-accordion').forEach(acc => {
            /** @type {any} */ (acc).ffInit?.();
          });

          // Reinit filter search inputs
          this.ffInitFilterSearch();

          // Reinit price range listeners (inputs were replaced)
          this.ffInitPriceRangeListener();
        }

        currentAccordion.style.display = '';
      });
    };

    /**
     * @param {NodeListOf<HTMLInputElement>} inputs
     */
    setMinAndMaxValues = (inputs) => {
      const minInput = inputs[0];
      const maxInput = inputs[1];
      if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
      if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
      if (minInput.value === "") maxInput.setAttribute("data-min", "0");
      if (maxInput.value === "")
        minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
    };

    /**
     * @param {HTMLInputElement} input
     */
    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute("data-min"));
      const max = Number(input.getAttribute("data-max"));

      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }

    /**
     * @param {URL} url
     * @param {boolean} [preservePage=false] - Whether to keep the page parameter
     */
    ffApplyUrl = async (url, preservePage = false) => {
      const productGrid = document.querySelector(".ff-product-grid-js");
      productGrid.classList.add("ff-shimmer");

      // Abort previous request if still pending
      if (this.ffAbortController) {
        this.ffAbortController.abort();
      }
      this.ffAbortController = new AbortController();
      const { signal } = this.ffAbortController;

      try {
        if (!preservePage) {
          url.searchParams.delete('page');
        }

        const response = await fetch(url, { signal });
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, "text/html");

        // Replace product grids
        const newGrids = html.querySelector(".ff-product-grid-js").querySelectorAll(".ff-product-grid-container-js");
        const currentGrids = productGrid.querySelectorAll(".ff-product-grid-container-js");

        newGrids.forEach((newGrid, index) => {
          const currentGrid = currentGrids[index];
          if (currentGrid) {
            currentGrid.replaceWith(newGrid);
          } else {
            productGrid.appendChild(newGrid);
          }
        });

        // Update filters in place (preserves accordion state)
        this.ffUpdateFiltersInPlace(html);

        const newCount = html.getElementById("ff-facets-results-count");
        if (newCount) {
          document.getElementById("ff-facets-results-count")?.replaceWith(newCount);
        }

        // Update data attributes
        /** @type {HTMLElement|null} */
        const newFacets = html.getElementById("ff-facets");
        if (newFacets) {
          this.dataset.ffTotalCount = newFacets.dataset.ffTotalCount || '';
          this.dataset.ffFilteredCount = newFacets.dataset.ffFilteredCount || '';
        }

        // Update pagination
        /** @type {HTMLElement|null} */
        const newPagination = html.querySelector(".ff-pagination-actions");
        /** @type {HTMLElement|null} */
        const currentPagination = document.querySelector(".ff-pagination-actions");

        if (newPagination && currentPagination) {
          currentPagination.replaceWith(newPagination);
          this.ffAddEvents(/** @type {HTMLElement} */ (document.querySelector(".ff-pagination-actions")));
        } else if (!newPagination && currentPagination) {
          currentPagination.remove();
        } else if (newPagination && !currentPagination) {
          const gridContainer = document.querySelector(".ff-product-grid-js");
          if (gridContainer) {
            gridContainer.appendChild(newPagination);
            this.ffAddEvents(/** @type {HTMLElement} */ (newPagination));
          }
        }

        // Re-observe sentinel for infinite scroll (sentinel was replaced)
        this.ffObserveSentinel();

        history.pushState(url.search, "", url.href);

        this.ffUpdateProductCount();
        this.ffUpdateActiveFiltersCount();
      } catch (error) {
        // Ignore AbortError - it's expected when we cancel previous requests
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error("Failed to update product grids:", error);
      } finally {
        // Only clear loading state if this wasn't an aborted request
        if (!signal.aborted) {
          requestAnimationFrame(() => {
            productGrid?.classList.remove("ff-shimmer");
            // Remove loading state from section container (includes block + logo)
            const navButtons = document.querySelector('ff-header-nav-buttons');
            const sectionContainer = navButtons?.closest('[class*="shopify-section"]') || navButtons?.closest('[class*="ff-collection-nav"]');
            sectionContainer?.classList.remove('ff-shimmer');
            this.ffClearLoadingState();
          });
        }
      }
    };
  }

  customElements.define("ff-facets", FfFacets);
}
